import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { combineLatest, filter, Subscription, switchMap, tap } from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';

import { Account } from '@shared/account/models';
import { BookingOptions } from '@shared/restaurant/models';
import { Service } from '@shared/service/models';
import { Resource } from '@shared/resource/models';
import { AuthUser } from '@shared/signup/models';
import { Location } from '@shared/location/models';
import { CancellationPolicyPeriod } from '@shared/location/enums';
import { getValidatorRequired } from '@tablesready/common-ui';
import { IDisplayOptions } from './reservation-options.interface';
import {
  reservationsPageViewList,
  resourceAutoAssignmentPrioritizationMethods,
} from './reservation-options.constant';
import {
  ResourcesService,
  ServicesService,
  WhoService,
  PricePlansRestrictionsService,
  PricePlansRestrictionsModalService,
} from '../../../services';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

const SECONDS_IN_MINUTE = 60;
const MILLISECONDS_IN_SECONDS = 1000;
const DEFAULT_BOOKING_INTERVAL = 900000;

@Component({
  selector: 'tablesready-reservation-options',
  templateUrl: './reservation-options.component.html',
  styleUrls: ['./reservation-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationOptionsComponent implements OnInit, OnDestroy {
  account: Account;
  location: Location;
  reservationTitle: string;
  reservationsTitle: string;
  reservationLabelOptions: string[];
  reservationViewOptions: Array<{ name: string; value: string }>;
  messagesContainLink: boolean;
  origin: string;
  displayOptions: IDisplayOptions;
  enabledServicesResources: boolean;
  onlineReservations: boolean;
  isDisabledOnlineReservations: boolean;
  publicReservationUrl = '';
  isLocationOnBusinessPlan = false;
  resourcePrioritizationMethods = resourceAutoAssignmentPrioritizationMethods;
  reservationOptionsForm: UntypedFormGroup;
  cancellationPolicyPeriod = CancellationPolicyPeriod;
  isMobileScreen = false;

  private bookingsAllowedPerTimeSlotQuantity = 1;
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly whoService: WhoService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly fb: UntypedFormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly pricePlansRestrictionsService: PricePlansRestrictionsService,
    private readonly pricePlansRestrictionsModalService: PricePlansRestrictionsModalService,
  ) { }

  ngOnInit(): void {
    this.origin = window.location.origin;

    const subscription = combineLatest([
      this.authRestaurantService.select(),
      this.servicesService.selectAll(),
      this.resourcesService.selectAll(),
    ])
      .pipe(
        filter(([user]) => user.location !== undefined),
        tap(([user, services, resources]) => this.onNewData(services, resources, user))
      )
      .subscribe();
    this.subscriptions.add(subscription);

    const screenSizeSub = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isMobileScreen }) => this.isMobileScreen = isMobileScreen)).subscribe();
    this.subscriptions.add(screenSizeSub);
  }

  onNewData(services, resources, { account, location }: AuthUser): void {
    this.account = account;
    this.location = location;
    this.reservationTitle = location.displayOptions?.labels?.reservation ?? 'reservation';
    this.reservationsTitle = location.displayOptions?.labels?.reservation_plural ?? 'reservation';
    this.reservationLabelOptions = ['reservation', 'appointment', 'booking'];
    this.reservationViewOptions = reservationsPageViewList;
    this.displayOptions = cloneDeep(this.location.displayOptions);
    this.onlineReservations = this.location.resourceSettings.enabled && this.location.serviceSettings.enabled &&
      Boolean(services?.filter(service => service.enabled).length) &&
      Boolean(this.getResourcesWithAvailableServices(services, resources).length);
    this.enabledServicesResources = this.location.resourceSettings.enabled && this.location.serviceSettings.enabled;
    this.messagesContainLink = this.checkMessagesContainLink();
    this.isLocationOnBusinessPlan = this.pricePlansRestrictionsService.availableForBusinessPlan(account.subscriptionType, location.recurly);

    const bookingOptions = this.location.bookingOptions;
    this.reservationOptionsForm = this.createReservationOptionsForm(bookingOptions, this.location);
    this.publicReservationUrl = `${this.origin}/p/book/add/${this.location.publicUrl}`;

    const onlineReservationsSub = this.reservationOptionsForm.get('onlineReservationsEnabled')
      .valueChanges
      .pipe(
        filter((value: boolean) => value && !this.isLocationOnBusinessPlan),
        switchMap(() => this.pricePlansRestrictionsModalService.display(this.authRestaurantService.get())),
        tap(() => {
          this.reservationOptionsForm.get('onlineReservationsEnabled')
            .setValue(false, { emitEvent: true });
        }),
      ).subscribe();
    this.subscriptions.add(onlineReservationsSub);

    this.cdr.markForCheck();
  }

  getResourcesWithAvailableServices(services: Service[], resources: Resource[]): Resource[] {
    return resources
      .filter(resource =>
        resource.services
          .some(serviceId =>
            services.find(service => service._id === serviceId)?.enabled
          ) && resource?.enabled
      );
  }

  fieldIsRequired(group: UntypedFormGroup) {
    if (group.get('enabled').value) {
      return Validators.required(group.get('quantity'));
    }

    return null;
  }

  checkMessagesContainLink(): boolean {
    return Object.keys(this.location.messages).some(key => this.hasLink(key));
  }

  hasLink(key): boolean {
    return ['smsReminder', 'smsResConfirmation'].includes(key) &&
      this.location.messages[key].enabled && this.location.messages[key].message.includes('{link}');
  }

  allowToDisableOnlineReservation(event): void {
    if (!event && this.messagesContainLink) {
      this.reservationOptionsForm.get('onlineReservationsEnabled').setValue(true);
      this.isDisabledOnlineReservations = !event;
    }
  }

  isReservationOptionsTouch(): boolean {
    return this.reservationOptionsForm.pristine;
  }

  submit(): void {
    const value = this.reservationOptionsForm.value;

    if (!this.reservationOptionsForm.get('reservationLabel').pristine) {
      this.displayOptions.togo = this.displayOptions.togo || false;
      this.displayOptions.labels.reservation = value.reservationLabel;
      this.displayOptions.labels.reservation_plural = `${value.reservationLabel}s`;
      this.subscriptions.add(this.whoService.updateDisplayOptions(this.displayOptions).subscribe(
        (response) => {
          this.alertsService.addSuccess(String(response));
        },
        (error) => {
          this.alertsService.addError(String(error?.error?.error));
        },
      ));
    }

    if (!this.isReservationOptionsTouch()) {
      this.reservationOptionsForm.markAsPristine();
      const reservationOptions = cloneDeep(value);
      reservationOptions.bookingInterval = (reservationOptions?.bookingInterval || 0) * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECONDS;
      delete reservationOptions.reservationLabel;
      reservationOptions.bookingsAllowedPerTimeSlot.quantity = reservationOptions.bookingsAllowedPerTimeSlot.quantity || this.bookingsAllowedPerTimeSlotQuantity;
      this.subscriptions.add(this.whoService.updateReservationSettings(reservationOptions).subscribe(
        () => {
          this.alertsService.addSuccess('Reservation defaults');
        },
        (error) => {
          this.alertsService.addError(String(error?.error?.error));
        },
      ));
    }
  }

  get cancellationTimeOptions(): number[] {
    const options = [];
    for (let i = 0; i < 37; i++) {
      options.push(i);
    }

    return options;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private createReservationOptionsForm(bo: BookingOptions, user: Location): UntypedFormGroup {
    const bookInterval = (bo.bookingInterval || DEFAULT_BOOKING_INTERVAL) / SECONDS_IN_MINUTE / MILLISECONDS_IN_SECONDS;
    let defaultPageView = this.reservationViewOptions[0].value;
    if (this.onlineReservations) {
      defaultPageView = bo.defaultPageView || this.reservationViewOptions[0].value;
    }

    return this.fb.group({
      reservationLabel: [user.displayOptions.labels.reservation || 'reservation'],
      allowMultipleParties: [bo.allowMultipleParties || false],
      bookingInterval: [bookInterval, [getValidatorRequired(), Validators.min(5)]],
      bookingLimiter: [bo.bookingLimiter, [getValidatorRequired(), Validators.min(0)]],
      bookingMinLimiter: [bo.bookingMinLimiter, [getValidatorRequired(), Validators.min(0)]],
      partiesLimiter: [bo.partiesLimiter, [getValidatorRequired(), Validators.pattern('^[0-9]*$'), Validators.min(1)]],
      bookingsAllowedPerTimeSlot: this.fb.group(
        {
          enabled: [bo.bookingsAllowedPerTimeSlot.enabled],
          quantity: [bo.bookingsAllowedPerTimeSlot.quantity, [Validators.min(1)]],
        },
        { validator: this.fieldIsRequired },
      ),
      onlineReservationsEnabled: [(bo.onlineReservationsEnabled || false) && this.onlineReservations],
      isRedirectModeEnabled: [bo.isRedirectModeEnabled],
      partySizeForTimeSlots: [bo.partySizeForTimeSlots],
      partySizeLimiter: [bo.partySizeLimiter, [getValidatorRequired(), Validators.pattern('^[0-9]*$'), Validators.min(1)]],
      resourceSelectionRequired: [bo.resourceSelectionRequired],
      sizeSelectionRequired: [bo.sizeSelectionRequired],
      timeSelectionWhenBooking: [bo.timeSelectionWhenBooking],
      servingTracking: [bo.servingTracking && this.onlineReservations],
      partyNotesOnTimeline: [bo.partyNotesOnTimeline || false],
      resourceAllocation: [bo.resourceAllocation],
      cancellationPolicy: this.fb.group({
        time: [bo.cancellationPolicy?.time],
        period: [bo.cancellationPolicy?.period],
      }),
      defaultPageView,
    });
  }
}
